import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentFlagService, getApiErrorMessage, PROFILE_FLAG_REASONS } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  flaggedId: number;
  flaggingId: number;
}

export const FlagProfileModal = ({
  isOpen,
  onClose,
  flaggedId,
  flaggingId,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [reasonId, setReasonId] = useState<number | ''>('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showError, setShowError] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReasonId('');
      setDescription('');
      setShowError(false);
      setSubmitted(false);
      setSubmitError('');
    }
  }, [isOpen]);

  const submit = async (): Promise<void> => {
    if (!reasonId || !description) {
      setShowError(true);
      return;
    }
    setSubmitting(true);
    try {
      await ContentFlagService.flagProfile({
        flaggedId,
        flaggingId,
        description,
        reason: { id: reasonId },
      });
      setSubmitted(true);
      setTimeout(() => onClose(), 3000);
    } catch (error) {
      setSubmitError(
        getApiErrorMessage(error) ?? t('components.modals.flagProfile.genericError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('components.modals.flagProfile.title')}
      className='w-full max-w-lg'
      footer={
        submitted ? undefined : (
          <>
            <Button buttonType={ButtonType.tertiary} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              buttonType={ButtonType.primary}
              disabled={submitting}
              onClick={submit}
            >
              {t('common.submit')}
            </Button>
          </>
        )
      }
    >
      {submitted ? (
        <div>
          <p className='mb-2'>{t('components.modals.flagProfile.thanksHeading')}</p>
          <p className='text-sm'>{t('components.modals.flagProfile.thanksFooter')}</p>
        </div>
      ) : (
        <>
          <select
            aria-label={t('components.modals.flagProfile.reasonAriaLabel')}
            className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
            value={reasonId}
            onChange={(e): void =>
              setReasonId(e.target.value ? Number(e.target.value) : '')
            }
          >
            <option value=''>{t('components.modals.flagProfile.pleaseSelect')}</option>
            {PROFILE_FLAG_REASONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.key === 'other'
                  ? t('common.other')
                  : t(`components.modals.flagProfile.reasons.${r.key}`)}
              </option>
            ))}
          </select>
          {showError && !reasonId && (
            <p className='text-red-600 text-sm mb-2'>
              {t('components.modals.flagProfile.reasonRequired')}
            </p>
          )}
          <textarea
            aria-label={t('components.modals.flagProfile.commentAriaLabel')}
            className='w-full border border-neutral-300 rounded p-2'
            rows={4}
            value={description}
            onChange={(e): void => setDescription(e.target.value)}
          />
          {showError && !description && (
            <p className='text-red-600 text-sm mt-1'>
              {t('components.modals.flagProfile.commentRequired')}
            </p>
          )}
          {submitError && (
            <p className='text-red-600 text-sm mt-2'>{submitError}</p>
          )}
          <p className='text-sm text-neutral-600 mt-3'>
            {t('components.modals.flagProfile.thanksFooter')}
          </p>
        </>
      )}
    </Modal>
  );
};
